'use client';

import React, { useEffect, useState } from 'react';
import { DollarSign, Play, TrendingUp, MessageCircle, ExternalLink, CheckCircle, Clock, XCircle } from 'lucide-react';
import { DEFAULT_DM_RULES, type DmRule, type MonetizationMetrics } from '@/lib/meta-api';

const MOCK_METRICS: MonetizationMetrics = {
  estimatedEarnings: 284.5,
  reelsPlays: 1840000,
  qualifiedPlays: 1324800,
  monetizationStatus: 'ACTIVE',
  bonusEarnings: 47.2,
  affiliateClicks: 3241,
  affiliateConversions: 312,
  dmsSent: 7841,
  conversionRate: 3.97,
};

function statusBadge(status: MonetizationMetrics['monetizationStatus']) {
  if (status === 'ACTIVE')
    return (
      <span className="flex items-center gap-1 text-positive text-xs font-semibold">
        <CheckCircle size={12} /> Active
      </span>
    );
  if (status === 'PENDING')
    return (
      <span className="flex items-center gap-1 text-warning text-xs font-semibold">
        <Clock size={12} /> Pending
      </span>
    );
  return (
    <span className="flex items-center gap-1 text-negative text-xs font-semibold">
      <XCircle size={12} /> Not Eligible
    </span>
  );
}

export default function MonetizationPanel() {
  const [metrics, setMetrics] = useState<MonetizationMetrics>(MOCK_METRICS);
  const [rules] = useState<DmRule[]>(DEFAULT_DM_RULES);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch('/api/meta/engagement?type=monetization')
      .then((r) => r.json())
      .then((data) => {
        if (!data.error) {
          setMetrics((prev) => ({
            ...prev,
            ...data,
            // Keep mock earnings/affiliate data until Partner API is connected
            estimatedEarnings: prev.estimatedEarnings,
            affiliateClicks: prev.affiliateClicks,
            affiliateConversions: prev.affiliateConversions,
            dmsSent: prev.dmsSent,
            conversionRate: prev.conversionRate,
          }));
        }
      })
      .catch(() => {/* use mock data */})
      .finally(() => setLoading(false));
  }, []);

  const qualifiedPct = metrics.reelsPlays > 0
    ? ((metrics.qualifiedPlays / metrics.reelsPlays) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="card-elevated p-5 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <DollarSign size={15} className="text-positive" />
            IG Monetization
          </h2>
          <p className="text-xs text-muted-foreground font-mono mt-0.5">
            Graph API · Live metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          {statusBadge(metrics.monetizationStatus)}
          {loading && (
            <span className="text-2xs text-muted-foreground font-mono animate-pulse">syncing…</span>
          )}
        </div>
      </div>

      {/* Earnings Row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-secondary rounded-lg p-3 border border-border">
          <p className="text-2xs text-muted-foreground font-mono uppercase tracking-wider">Est. Earnings</p>
          <p className="text-2xl font-bold tabular-nums text-positive mt-1">
            ${metrics.estimatedEarnings.toFixed(2)}
          </p>
          <p className="text-2xs text-muted-foreground font-mono mt-0.5">this month</p>
        </div>
        <div className="bg-secondary rounded-lg p-3 border border-border">
          <p className="text-2xs text-muted-foreground font-mono uppercase tracking-wider">Bonus</p>
          <p className="text-2xl font-bold tabular-nums text-accent mt-1">
            ${metrics.bonusEarnings.toFixed(2)}
          </p>
          <p className="text-2xs text-muted-foreground font-mono mt-0.5">creator bonus</p>
        </div>
      </div>

      {/* Reels Plays */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-1.5 text-muted-foreground font-mono">
            <Play size={11} /> Reels Plays
          </span>
          <span className="font-semibold tabular-nums text-foreground">
            {(metrics.reelsPlays / 1_000_000).toFixed(2)}M
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-1.5">
          <div
            className="bg-primary h-1.5 rounded-full transition-all duration-500"
            style={{ width: `${qualifiedPct}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-2xs text-muted-foreground font-mono">
          <span>{qualifiedPct}% qualified plays</span>
          <span>{(metrics.qualifiedPlays / 1000).toFixed(0)}K monetized</span>
        </div>
      </div>

      {/* Affiliate Stats */}
      <div className="border-t border-border pt-4 space-y-2">
        <p className="text-2xs font-semibold uppercase tracking-widest text-muted-foreground">
          Affiliate Pipeline
        </p>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold tabular-nums text-foreground">
              {metrics.dmsSent.toLocaleString()}
            </p>
            <p className="text-2xs text-muted-foreground font-mono">DMs sent</p>
          </div>
          <div>
            <p className="text-lg font-bold tabular-nums text-info">
              {metrics.affiliateClicks.toLocaleString()}
            </p>
            <p className="text-2xs text-muted-foreground font-mono">link clicks</p>
          </div>
          <div>
            <p className="text-lg font-bold tabular-nums text-positive">
              {metrics.affiliateConversions}
            </p>
            <p className="text-2xs text-muted-foreground font-mono">conversions</p>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs mt-1">
          <span className="text-muted-foreground font-mono">Conv. rate</span>
          <span
            className={`font-semibold tabular-nums ${
              metrics.conversionRate >= 5 ? 'text-positive' : 'text-negative'
            }`}
          >
            {metrics.conversionRate.toFixed(2)}%
          </span>
        </div>
      </div>

      {/* Auto-DM Rules */}
      <div className="border-t border-border pt-4 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-2xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <MessageCircle size={11} /> Auto-DM Rules
          </p>
          <span className="text-2xs font-mono text-positive">
            {rules.filter((r) => r.active).length} active
          </span>
        </div>
        <div className="space-y-1.5">
          {rules.map((rule) => (
            <div
              key={rule.id}
              className="flex items-center justify-between px-3 py-2 rounded-md bg-secondary border border-border text-xs"
            >
              <div className="flex items-center gap-2">
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    rule.active ? 'bg-positive' : 'bg-muted-foreground'
                  }`}
                />
                <span className="font-mono text-foreground">"{rule.keyword}"</span>
              </div>
              <a
                href={rule.affiliateLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-primary hover:text-primary/80 transition-colors"
              >
                link <ExternalLink size={10} />
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Engagement Tracking Note */}
      <div className="border-t border-border pt-3">
        <div className="flex items-center gap-2 text-2xs text-muted-foreground font-mono">
          <TrendingUp size={11} className="text-primary shrink-0" />
          <span>
            Webhook: <code className="text-foreground">/api/meta/webhook</code> · Publish:{' '}
            <code className="text-foreground">/api/meta/publish</code>
          </span>
        </div>
      </div>
    </div>
  );
}
